/**
 * Exact child-request capability persisted for public multi-hierarchy drill lock (BUD-18770).
 *
 * Mirrors the fields sent on `POST /next/pivot/rows` when `parentQuery` is false. Root parent
 * queries are always allowed for anonymous public viewers; child drills must match a capability
 * exactly. Absent/empty capabilities mean root-only.
 */
export interface PublicMhDrillCapability {
  rowGroupId: string;
  groupingValues: string[];
  rowBreakdownLevel?: number;
  /**
   * Present only when true — the drill expands a deepest-level leaf group into its leaf detail
   * rows (account numbers, or segment codes when no Account Number level is configured). Matches
   * fetchChildRows payload semantics.
   */
  expandLeafDetail?: true;
}

/**
 * A capability as it may sit in a saved report configuration. Capabilities recorded before
 * BUD-18823 named the leaf-detail flag `expandLeafAccounts`; nothing writes that key any more, but
 * saved reports keep it until the creator toggles the row again (a plain re-save does not rewrite
 * capabilities).
 */
type PersistedPublicMhDrillCapability = PublicMhDrillCapability & { readonly expandLeafAccounts?: boolean };

export function normalizePublicMhDrillCapability(
  capability: PersistedPublicMhDrillCapability
): PublicMhDrillCapability {
  const groupingValues = capability.groupingValues.map((value) => value ?? '');
  // The old key only ever meant "expand this leaf into its accounts", which is exactly what
  // `expandLeafDetail` means for an Account Number config — reading it grants nothing new.
  const expandsLeafDetail = capability.expandLeafDetail === true || capability.expandLeafAccounts === true;
  return {
    rowGroupId: capability.rowGroupId,
    groupingValues,
    ...(capability.rowBreakdownLevel !== undefined ? { rowBreakdownLevel: capability.rowBreakdownLevel } : {}),
    ...(expandsLeafDetail ? { expandLeafDetail: true as const } : {})
  };
}

export function publicMhDrillCapabilitiesEqual(
  left: PublicMhDrillCapability,
  right: PublicMhDrillCapability
): boolean {
  const a = normalizePublicMhDrillCapability(left);
  const b = normalizePublicMhDrillCapability(right);
  if (a.expandLeafDetail !== b.expandLeafDetail) return false;
  return publicMhDrillRowsEqual(a, b);
}

export function isGroupingValuesPrefix(
  parentGroupingValues: string[],
  candidateGroupingValues: string[]
): boolean {
  if (candidateGroupingValues.length <= parentGroupingValues.length) return false;
  return parentGroupingValues.every((value, index) => value === candidateGroupingValues[index]);
}

export function addPublicMhDrillCapability(
  capabilities: PublicMhDrillCapability[] | undefined,
  capability: PublicMhDrillCapability
): PublicMhDrillCapability[] {
  const normalized = normalizePublicMhDrillCapability(capability);
  const existing = capabilities ?? [];
  if (existing.some((item) => publicMhDrillCapabilitiesEqual(item, normalized))) {
    return existing;
  }
  return [...existing, normalized];
}

export function removePublicMhDrillCapabilityAndDescendants(
  capabilities: PublicMhDrillCapability[] | undefined,
  collapsed: PublicMhDrillCapability
): PublicMhDrillCapability[] {
  const parent = normalizePublicMhDrillCapability(collapsed);
  return (capabilities ?? []).filter((item) => {
    const candidate = normalizePublicMhDrillCapability(item);
    if (candidate.rowGroupId !== parent.rowGroupId) return true;
    if (publicMhDrillCapabilitiesEqual(candidate, parent)) return false;
    return !isGroupingValuesPrefix(parent.groupingValues, candidate.groupingValues);
  });
}

/** Fields taken from the request body. Attacker-controlled — never trust these for scoping. */
export type PublicMhDrillRequest = {
  parentQuery?: boolean;
  rowGroupId: string | undefined;
  groupingValues?: string[];
  rowBreakdownLevel?: number;
  expandLeafDetail?: boolean;
};

/**
 * Fields read from the persisted report configuration. Kept in a separate parameter from
 * `PublicMhDrillRequest` so the trust boundary is visible in the signature: a caller cannot
 * accidentally populate the allowlist from the request body, which would turn the whole control
 * into a caller-supplied one.
 */
export type PublicMhDrillPersistedConfig = {
  capabilities: PublicMhDrillCapability[] | undefined;
  /** Configured group ids per grouping level, in level order. */
  groupIdsByLevel?: string[][];
  /** Whether the report injects breakdown rows at root level, which prefetch their own children. */
  showRootLevelBreakdowns?: boolean;
};

/**
 * Whether the drill is the automatic prefetch behind a root-level breakdown row.
 *
 * With `showRootLevelBreakdowns` on, the client injects breakdown rows at level 0 and fetches their
 * children unprompted on every load so their totals render — nobody expanded them, so no capability
 * exists and the guard would reject every anonymous load of such a report. Those rows and their
 * values are already on screen by configuration, so allowing that one fetch reveals nothing new.
 *
 * Deliberately matched to the prefetch's exact shape rather than to "ids that appear somewhere in
 * the config": it sends precisely two grouping values, the `${siblingId}:${selfId}` pair split out
 * of the injected row's node key, at that row's own breakdown level. Accepting any length, any
 * order or any level would permit slices of the hierarchy the creator never exposed.
 */
export function configuredRootBreakdownPermitsDrill(
  request: PublicMhDrillRequest,
  persisted: PublicMhDrillPersistedConfig
): boolean {
  if (persisted.showRootLevelBreakdowns !== true) return false;
  // Leaf-detail rows are never part of a breakdown prefetch, and are refused on public reports.
  if (request.expandLeafDetail === true) return false;

  const level = request.rowBreakdownLevel;
  if (level === undefined || level < 1) return false;

  const groupingValues = request.groupingValues ?? [];
  if (groupingValues.length !== 2) return false;

  const levels = persisted.groupIdsByLevel ?? [];
  const rootLevelIds = levels[0] ?? [];
  const breakdownLevelIds = levels[level] ?? [];

  return rootLevelIds.includes(groupingValues[0]) && breakdownLevelIds.includes(groupingValues[1]);
}

/**
 * Returns true when an anonymous public MH drill request is allowed.
 * Root (`parentQuery === true`) only. Non-parent empty/absent paths are denied.
 */
export function isPublicMhDrillRequestPermitted(
  request: PublicMhDrillRequest,
  persisted: PublicMhDrillPersistedConfig
): boolean {
  if (request.parentQuery === true) return true;

  const groupingValues = request.groupingValues ?? [];
  if (groupingValues.length === 0) return false;
  if (!request.rowGroupId) return false;

  if (configuredRootBreakdownPermitsDrill(request, persisted)) return true;

  // Absent and empty both mean root-only. There is no "recorded later" state to allow for: the
  // creator's expansions write capabilities as they happen, so a report with none has none.
  if (!persisted.capabilities || persisted.capabilities.length === 0) return false;

  const requested = normalizePublicMhDrillCapability({
    rowGroupId: request.rowGroupId,
    groupingValues,
    ...(request.rowBreakdownLevel !== undefined ? { rowBreakdownLevel: request.rowBreakdownLevel } : {})
  });

  return persisted.capabilities.some((capability) => publicMhDrillRowsEqual(capability, requested));
}

/**
 * Whether two capabilities name the same row: same row group, same drill path, same breakdown level.
 *
 * The leaf-detail flag is deliberately left out of this comparison. It does not say WHICH row may be
 * opened — the creator exposed the row by expanding it — but what opening it shows, and that is decided
 * by the report's configuration at request time: a row the creator expanded into sub-groups under
 * "Full Hierarchy" expands into its codes once the level is set to "Leaf only" (BUD-19598), and a
 * capability saved before BUD-18823 carries no flag at all. Matching the flag exactly made every such
 * switch a 403 for anonymous viewers until the creator re-expanded each row by hand.
 */
export function publicMhDrillRowsEqual(left: PublicMhDrillCapability, right: PublicMhDrillCapability): boolean {
  const a = normalizePublicMhDrillCapability(left);
  const b = normalizePublicMhDrillCapability(right);
  if (a.rowGroupId !== b.rowGroupId) return false;
  if (a.rowBreakdownLevel !== b.rowBreakdownLevel) return false;
  if (a.groupingValues.length !== b.groupingValues.length) return false;
  return a.groupingValues.every((value, index) => value === b.groupingValues[index]);
}
